import { useLocalStorage } from "PosterEditor/hooks/use-locale-storage";
import { useState, useEffect } from "react";
import { ProjectStore } from "PosterEditor/model/poster-project";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { v4 as uuid } from "uuid";
import { defaultProjectTemplate } from "PosterEditor/model/default-project-style";

export const useTemplateLocalStore = () => {
  const { save, read, remove } = useLocalStorage();
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);

  useEffect(() => {
    loadTemplates();
  }, []);

  const createTemplate = () => {
    const template = { ...defaultProjectTemplate, id: uuid() };
    saveTemplate(template);
    return template;
  };

  const deleteTemplate = (template: ProjectTemplate) => {
    setTemplates((prev) => {
      const newTemplates = prev ? [...prev] : [];
      const index = newTemplates.findIndex((t) => t.id === template.id);

      let updatedTemplates;
      if (index !== -1) {
        updatedTemplates = newTemplates.filter((t) => t.id !== template.id);
      } else {
        updatedTemplates = [...newTemplates];
      }

      save("templateStore", updatedTemplates);
      return [...updatedTemplates];
    });
  };

  const deleteAllTemplates = () => {
    setTemplates([]);
    remove("templateStore");
  };

  const saveTemplate = (template: ProjectTemplate) => {
    const templates = loadTemplates();
    if (!templates) {
      setTemplates([template]);
      save("templateStore", [template]);

      return;
    }
    const newTemplates = templates;
    const index = templates.findIndex((p) => p.id === template.id);
    let updatedTemplates;
    if (index !== -1) {
      updatedTemplates = newTemplates.map((t) =>
        t.id === template.id ? template : t
      );
    } else {
      updatedTemplates = [{ ...template }, ...newTemplates];
    }
    const sortedTemplates = updatedTemplates.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    save("templateStore", sortedTemplates);
    return sortedTemplates; // ✅ Creates a new array reference
  };

  const loadTemplates = () => {
    const templates = read("templateStore") as ProjectTemplate[] | undefined;

    if (!templates) {
      setTemplates([]);
      return;
    }

    const sortedTemplates = templates.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setTemplates(sortedTemplates);
    return sortedTemplates;
  };

  const loadTemplate = (id: string) => {
    const templates = read("templateStore") as ProjectTemplate[] | undefined;
    return templates?.filter((template) => template.id === id)[0];
  };

  return {
    loadTemplates,
    loadTemplate,
    saveTemplate,
    createTemplate,
    deleteTemplate,
    deleteAllTemplates,
    templates,
  };
};
